import { EmitedPolicy } from "@/services/common/emission-types";
import React, { createContext, useContext, useEffect, useState } from "react";

interface DetailPolicyContextType {
    policy: EmitedPolicy | null;
    setPolicy: React.Dispatch<React.SetStateAction<EmitedPolicy | null>>;
    policyId: string;
    setPolicyId: React.Dispatch<React.SetStateAction<string>>;
    getStoredPolicyById: (policyId: string) => EmitedPolicy | null;
    clearContext: () => void;
}

const initialContextValue: DetailPolicyContextType = {
    policy: null,
    setPolicy: () => null,
    policyId: "",
    setPolicyId: () => "",
    getStoredPolicyById: () => null,
    clearContext: () => undefined
};

const DetailPolicyContext = createContext<DetailPolicyContextType>(initialContextValue);

interface DetailPolicyProviderProps {
    children: React.ReactNode;
}

export const DetailPolicyProvider: React.FC<DetailPolicyProviderProps> = ({ children }) => {
    const [policy, setPolicy] = useState<EmitedPolicy | null>(null);
    const [policyId, setPolicyId] = useState("");

    // Persistencia en localStorage
    useEffect(() => {
        if (!policy || !policyId) return;
        try {
            localStorage.setItem(`policy_${policyId}`, JSON.stringify(policy));
        } catch (error) {
            console.error('Error al guardar en localStorage:', error);
        }
    }, [policy, policyId]);

    // Recuperación inicial de datos
    useEffect(() => {
        if (!policyId) return;

        try {
            const storedPolicy = localStorage.getItem(`policy_${policyId}`);
            if (storedPolicy && storedPolicy !== "undefined") {
                setPolicy(JSON.parse(storedPolicy));
            }
        } catch (error) {
            console.error('Error al recuperar de localStorage:', error);
        }
    }, [policyId]);

    // Función para obtener póliza por ID
    const getStoredPolicyById = (id: string): EmitedPolicy | null => {
        if (!id) return null;

        try {
            const storedPolicy = localStorage.getItem(`policy_${id}`);
            if (storedPolicy && storedPolicy !== "undefined") {
                return JSON.parse(storedPolicy);
            }
        } catch (error) {
            console.error('Error al recuperar datos de la póliza:', error);
        }
        return null;
    };

    // Función para limpiar el contexto
    const clearContext = () => {
        setPolicy(null);
        setPolicyId("");
    };

    const contextValue = {
        policy,
        setPolicy,
        policyId,
        setPolicyId,
        getStoredPolicyById,
        clearContext
    };

    return (
        <DetailPolicyContext.Provider value={contextValue}>
            {children}
        </DetailPolicyContext.Provider>
    );
};

export const useDetailPolicy = () => {
    const context = useContext(DetailPolicyContext);
    if (!context) {
        throw new Error("useDetailPolicy debe ser usado dentro de un DetailPolicyProvider");
    }
    return context;
};
